import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AgGridReact } from '@ag-grid-community/react'
import {
  GridReadyEvent,
  IServerSideGetRowsParams,
} from '@ag-grid-community/core'
import { Stack } from '@sureco/design-system'
import { getHrisCensusReportFiles } from '../../../api/hris-api-client'
import { SortDirection } from '../../../../../types/sorted-request.dto'
import { DEFAULT_CENSUS_FILES_PAGINATION_LIMIT } from '../../../constants'
import { CensusFileWithRowStatsDto } from '../../../../CensusManagement/api/dto/census-file-with-row-stats.dto'
import { withRouter, routerShape } from 'react-router'
import { getCensusFilesColDefs } from '../../../utils/get-census-files-col-defs'
import { SimpleCustomerDto } from '../../../../../api/persons/dto/simple-customer.dto'
import { getMyClients } from '../../../../../api/persons'

type Props = {
  clientNameLookup: Record<string, string>
  router: routerShape
  fileUpdated: string
  selectedClientId?: string
  onFilesChanged?: () => void
}

const AssistedManualFilesGrid = ({
  selectedClientId,
  clientNameLookup,
  fileUpdated,
  onFilesChanged,
}: Props) => {
  const [clients, setClients] = useState<SimpleCustomerDto[]>([])
  const [grid, setGrid] = useState<GridReadyEvent>()
  const clientIds = clients.map((client) => client?.id)
  // purge: false refetches and updates row data in place instead of
  // destroying/recreating every visible row's cell renderer — a purge here
  // would unmount an in-flight submit's GeneratedFilesCellRenderer (eg. when
  // triggered by an unrelated row's submit completing or a Zendesk send
  // finishing), silently resetting its loading guard before the request it
  // was tracking has actually finished.
  const refreshGrid = useCallback(() => {
    if (grid) {
      grid.api.refreshServerSide({ purge: false })
    }
    // A queued submit shows up in the page's Queue section
    onFilesChanged?.()
  }, [grid, onFilesChanged])

  useEffect(() => {
    const getClients = async () => {
      const customers: SimpleCustomerDto[] = await getMyClients()
      setClients(customers)
    }
    getClients()
  }, [])

  const datasource = useMemo(
    () => ({
      getRows: async (
        params: IServerSideGetRowsParams<CensusFileWithRowStatsDto>
      ) => {
        const limit = DEFAULT_CENSUS_FILES_PAGINATION_LIMIT
        let sort: string | undefined
        let order: SortDirection | undefined
        try {
          const { startRow, sortModel } = params.request
          if (sortModel.length > 0) {
            const { colId, sort: direction } = sortModel[0] // Take the first sort entry
            sort = colId
            order =
              direction === 'desc' ? SortDirection.desc : SortDirection.asc
          }
          const page = Math.floor((startRow ?? 1) / limit) + 1

          const { meta: pagination, data: files } =
            await getHrisCensusReportFiles({
              page,
              limit,
              sort,
              order,
              clientPublicKey: selectedClientId
                ? [selectedClientId]
                : undefined,
            })

          params.success({ rowData: files, rowCount: pagination.total })
        } catch (error) {
          console.error(error)
          params.fail()
        }
      },
    }),
    [clientIds, clientNameLookup]
  )

  useEffect(() => {
    if (grid) {
      grid.api.refreshServerSide({ purge: false })
    }
  }, [fileUpdated, grid])

  useEffect(() => {
    if (grid) {
      grid.api.setGridOption('serverSideDatasource', datasource)
      grid.api.purgeInfiniteCache()
      grid.api.refreshServerSide({ purge: false })
    }
  }, [grid, datasource])

  const columnDefs = useMemo(
    () =>
      getCensusFilesColDefs({
        refreshGrid,
        hasSubmittedColumn: false,
        clientNameLookup,
      }),
    [refreshGrid, clientNameLookup]
  )

  return (
    <>
      <Stack direction="row" align="center" className="subtitle">
        Census
      </Stack>
      <AgGridReact
        rowStyle={{
          backgroundColor: 'white',
          marginBottom: '20px',
        }}
        className="assistedManualCensusGrid ag-theme-alpine"
        getRowId={({ data }) => data.id}
        pagination
        paginationPageSize={5}
        cacheBlockSize={5}
        columnDefs={columnDefs}
        rowModelType="serverSide"
        onGridReady={(params) => setGrid(params)}
        domLayout="autoHeight"
        suppressHorizontalScroll
        paginationPageSizeSelector={[5, 10, 20, 100]}
      />
      <hr className="page-hr" style={{ marginTop: '10px' }} />
    </>
  )
}

export default withRouter(AssistedManualFilesGrid)
